import {
  CircleCheck,
  EyeOff,
  Loader2,
  type LucideIcon,
  NotebookPen,
  RotateCcw,
  Wrench,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type IssueAction,
  type IssueStatus,
  type IssueStatusAction,
  useMcpIssueAction,
  useMcpIssueNote,
} from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

/** The longest note the backend keeps, in characters. */
export const NOTE_MAX = 2000;

/** Each action's icon. */
const ICONS: Record<IssueAction, LucideIcon> = {
  acknowledge: Wrench,
  resolve: CircleCheck,
  ignore: EyeOff,
  reopen: RotateCcw,
  note: NotebookPen,
};

/**
 * The status changes an issue offers in its status, the likely next one
 * first: an issue to analyse can be taken on, resolved or ignored; one a
 * developer is on resolved, ignored or opened again; a settled one opened
 * again.
 */
function statusActions(status: IssueStatus): IssueStatusAction[] {
  switch (status) {
    case "new":
    case "open":
    case "regressed":
      return ["acknowledge", "resolve", "ignore"];
    case "acknowledged":
      return ["resolve", "ignore", "reopen"];
    case "resolved":
    case "ignored":
      return ["reopen"];
  }
}

/**
 * The actions an issue offers someone: a developer its status changes
 * (`statusActions`) and a note, any other admin role a note alone. A note
 * fits every status and comes last.
 */
export function issueActions(status: IssueStatus, developer: boolean): IssueAction[] {
  return developer ? [...statusActions(status), "note"] : ["note"];
}

/**
 * The key of an action's words in a status (`issues.actions.<key>` for its
 * button and `issues.dialog.<key>` for what it does): its own ("Heropenen"),
 * except opening again an issue a developer is on, which takes it back to
 * Open ("Terug naar open") rather than reopening something closed.
 */
export function issueActionWords(
  action: IssueAction,
  status: IssueStatus,
): IssueAction | "release" {
  return action === "reopen" && status === "acknowledged" ? "release" : action;
}

/** Why a note cannot be sent, or null when it can. */
export function noteProblem(action: IssueAction, note: string): "required" | "tooLong" | null {
  if (note.length > NOTE_MAX) {
    return "tooLong";
  }
  return action === "note" && note.trim() === "" ? "required" : null;
}

/** The note box's classes, as the dashboard's other text boxes. */
const TEXTAREA_CLASS =
  "border-input bg-card text-foreground ring-offset-background placeholder:text-muted focus-visible:ring-ring flex min-h-24 w-full resize-y rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50";

interface IssueActionsProps {
  issueKey: string;
  status: IssueStatus;
  /** The period the page shows, so the answer replaces what is on screen. */
  days: number;
  /** Whether the reader is a developer, who also changes the status. */
  developer: boolean;
}

/**
 * The actions on an issue, as buttons under its heading (`issueActions`): for
 * a developer "In behandeling nemen", "Oplossen", "Negeren", "Heropenen"
 * ("Terug naar open" for an issue in progress, `issueActionWords`) as the
 * status allows, the first one solid, and for every admin role "Notitie
 * toevoegen". Each opens a small dialog with a note for the issue's history,
 * optional for a status change and needed for a note. The backend takes a
 * status change from developers only and a note from every admin role.
 */
export function IssueActions({ issueKey, status, days, developer }: IssueActionsProps) {
  const { t } = useTranslation();
  const [action, setAction] = useState<IssueAction | null>(null);
  const [open, setOpen] = useState(false);
  // Each opening gets a fresh dialog, with an empty note.
  const [opened, setOpened] = useState(0);
  const groupRef = useRef<HTMLDivElement>(null);
  const actions = issueActions(status, developer);

  return (
    <>
      <div
        ref={groupRef}
        role="group"
        aria-label={t("issues.actions.label")}
        className="flex flex-wrap gap-2"
      >
        {actions.map((item, index) => {
          const Icon = ICONS[item];
          return (
            <Button
              key={item}
              size="sm"
              // The likely next status change solid; a note never.
              variant={index === 0 && item !== "note" ? "default" : "outline"}
              data-action={item}
              onClick={() => {
                setAction(item);
                setOpened((count) => count + 1);
                setOpen(true);
              }}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {t(`issues.actions.${issueActionWords(item, status)}`)}
            </Button>
          );
        })}
      </div>
      {action && (
        <ActionDialog
          key={opened}
          issueKey={issueKey}
          action={action}
          words={issueActionWords(action, status)}
          days={days}
          open={open}
          onOpenChange={setOpen}
          // The dialog has no trigger: give the focus back to the action's button, or to the
          // first action when the new status no longer offers it.
          returnFocus={() =>
            (
              groupRef.current?.querySelector<HTMLElement>(`[data-action="${action}"]`) ??
              groupRef.current?.querySelector<HTMLElement>("button")
            )?.focus()
          }
        />
      )}
    </>
  );
}

interface ActionDialogProps {
  issueKey: string;
  action: IssueAction;
  /** Its words in the issue's status (`issueActionWords`). */
  words: IssueAction | "release";
  days: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocus: () => void;
}

/**
 * Confirms one action, with a note for the history: optional for a status
 * change, needed for a note, which goes to its own endpoint. While the
 * request runs the dialog cannot be dismissed, so nobody believes they
 * cancelled an action that still lands.
 */
function ActionDialog({
  issueKey,
  action,
  words,
  days,
  open,
  onOpenChange,
  returnFocus,
}: ActionDialogProps) {
  const { t } = useTranslation();
  const noteId = useId();
  const hintId = useId();
  const statusMutation = useMcpIssueAction();
  const noteMutation = useMcpIssueNote();
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const pending = statusMutation.isPending || noteMutation.isPending;
  const problem = noteProblem(action, note);
  const shownProblem = problem === "tooLong" || (tried && problem) ? problem : null;

  const dismiss = (next: boolean) => {
    if (!next && pending) {
      return;
    }
    if (!next) {
      setFailure(null);
    }
    onOpenChange(next);
  };

  const confirm = async () => {
    setTried(true);
    if (problem) {
      return;
    }
    setFailure(null);
    try {
      if (action === "note") {
        await noteMutation.mutateAsync({ key: issueKey, note, days });
      } else {
        await statusMutation.mutateAsync({ key: issueKey, action, note, days });
      }
      onOpenChange(false);
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={dismiss}>
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t(`issues.actions.${words}`)}</DialogTitle>
          <DialogDescription>{t(`issues.dialog.${words}`)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label htmlFor={noteId} className="text-foreground block text-sm font-medium">
            {action === "note" ? t("issues.dialog.noteLabel") : t("issues.dialog.noteOptional")}
          </label>
          <textarea
            id={noteId}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            aria-invalid={shownProblem !== null}
            aria-describedby={hintId}
            disabled={pending}
            className={cn(TEXTAREA_CLASS, shownProblem && "border-destructive")}
          />
          <p
            id={hintId}
            className={cn("text-xs", shownProblem ? "text-destructive" : "text-muted")}
          >
            {shownProblem === "required"
              ? t("issues.dialog.noteRequired")
              : shownProblem === "tooLong"
                ? t("issues.dialog.noteTooLong", { max: formatNumber(NOTE_MAX) })
                : t("issues.dialog.noteHint")}
          </p>
        </div>
        {failure && (
          <p role="alert" className="text-destructive text-sm">
            {apiErrorMessage(failure.error, "issueAction")}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => dismiss(false)} disabled={pending}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void confirm()} disabled={pending}>
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-label={t("common.loading")} />
            ) : (
              t(`issues.actions.${words}`)
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
